/**
 * Modal dialog to enter an email to invite as member.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */
import { fetchUserComplete, inviteSubUser } from '@/api'
import ModalDialog from '@/components/ModalDialog'
import { logger } from '@/config/logger'
import { Util } from '@amplify3/duplo-back-end'
import axios from 'axios'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { BiLoaderAlt } from 'react-icons/bi'
import * as Yup from 'yup'
import './InviteMemberDialog.css'

interface InviteMemberDialogProps {
  company_id: string
  isShowing: boolean
  show: (isShowing: boolean) => void
}

/**
 * A modal dialog to enter an email to invite as member.
 */
const InviteMemberDialog = (props: InviteMemberDialogProps) => {
  const [isProcessing, setProcessing] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const INITIAL_VALUES = { invite_email: '' }

  /**
   * Clear error message at the opening of the dialog.
   */
  useEffect(() => {
    if (props.isShowing) {
      setRequestError(null)
    }
  }, [props.isShowing])

  /**
   * Handle the sending of the invitation.
   */
  const onSubmit = async (values: any) => {
    if (!isProcessing) {
      setProcessing(true)
      try {
        logger.info(`Send invitation to ${values.invite_email}`)
        await inviteSubUser(props.company_id, values.invite_email)
        fetchUserComplete(true)
        props.show(false)
        setRequestError(null)
      } catch (err) {
        const message = axios.isAxiosError(err)
          ? (err.response?.data?.message ?? 'Failed to send invitation.')
          : 'Failed to send invitation.'
        setRequestError(message)
      } finally {
        await Util.sleep(500)
        setProcessing(false)
      }
    }
  }

  const emailValidator = Yup.object({
    invite_email: Yup.string().email('Invalid email').required('Required'),
  })

  return (
    <ModalDialog
      className="invite-dialog"
      title="Invite member"
      isVisible={props.isShowing}
      changeVisible={props.show}
      maxWidth={'sm'}
      contents={
        <div className="contents">
          {/*
          The contents compoent of ModalDialog must be defined inside
          ModalDialog.

          If defined outside, every time isProcessing state changes
          (on submit click), React sees a brand new component type,
          unmounts the old one, and mounts a fresh one — resetting Formik's
          state including the field value.
          */}

          <Formik
            onSubmit={onSubmit}
            initialValues={INITIAL_VALUES}
            validationSchema={emailValidator}
          >
            {(
              // Declare needed built-in Formik's own handlers
              { isValid, dirty, handleChange }
            ) => (
              <Form>
                <div className="invite-row">
                  <label htmlFor="invite_email" className="invite-label">
                    Email:
                  </label>
                  <Field
                    type="text"
                    id="invite_email"
                    name="invite_email"
                    // Use ircam-input class for the design of the field
                    className="ircam-input invite-email"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      // Clear the request error message at each change of the email
                      handleChange(e)
                      setRequestError(null)
                    }}
                  />
                </div>

                {/* Error messages in the same container */}
                <div className="error-display">
                  <ErrorMessage name="invite_email" />
                  {requestError}
                </div>

                <div className="invite-actions">
                  <button
                    type="submit"
                    className="send"
                    disabled={!isValid || !dirty}
                  >
                    {isProcessing ? (
                      <BiLoaderAlt className="loadingIcon" />
                    ) : (
                      'Send'
                    )}
                  </button>
                  <button
                    type="button"
                    className="cancel"
                    onClick={() => props.show(false)}
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      }
    ></ModalDialog>
  )
}

export default InviteMemberDialog
